import { api } from "../../api"
import { NotificationChannel, NotificationType } from "../../notifications/notifications"
import { BillingSettingsFormValues } from "../components/billing-settings-form/use-billing-settings-form"
import { BillingSettingsResponse, BillingSettingsResponseSchema } from "./billing-settings-response"
import { makeOrganisationApiEndpoints } from "./endpoints"
import { CheckBillingSettingsViewModel, OrganisationViewModel, makeCheckBillingSettingsViewModel } from "./organisation"
import { CheckBillingSettingsResponse } from "./organisation-response"
import { makeProjectTypeViewModel, ProjectTypeViewModel } from "./project-type"
import { ProjectTypeResponse, SystemStatusId } from "./project-type-response"
import {
  HourReportSettings,
  HourReportSettingsViewModel,
  makeHourReportSettingsViewModel,
} from "./hour-report-settings"

export { HourReportWorkflow } from "./hour-report-settings"

const apiEndpoints = makeOrganisationApiEndpoints()

export const organisationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrganisationById: builder.query<OrganisationViewModel, string>({
      query: (id) => apiEndpoints.organisationItem(id),
      providesTags: ["Organisation"],
    }),
    checkBillingSettings: builder.query<CheckBillingSettingsViewModel, string>({
      query: (id) => apiEndpoints.orgBillingSettingsCheck(id),
      transformResponse: (checkBillingSettings: CheckBillingSettingsResponse) => {
        return makeCheckBillingSettingsViewModel(checkBillingSettings)
      },
    }),
    getBillingSummary: builder.query<BillingSummaryResponse, string>({
      query: (id) => apiEndpoints.billingSummary(id),
    }),
    billingSummaryAndCostEstimate: builder.query<
      BillingSummaryResponse,
      { orgId: string; scenarios: CostEstimationScenario[] }
    >({
      query: ({ orgId, scenarios }) => ({
        url: apiEndpoints.billingSummary(orgId),
        method: "POST",
        body: { scenarios },
      }),
    }),
    estimateCost: builder.query<CostEstimationResponse, { orgId: string; scenarios: CostEstimationScenario[] }>({
      query: ({ orgId, scenarios }) => ({
        url: apiEndpoints.estimateCost(orgId),
        method: "POST",
        body: { scenarios },
      }),
    }),
    updateOrgNotificationSettings: builder.mutation<
      OrganisationViewModel,
      { orgId: string } & OrgNotificationSettingsUpdateData
    >({
      query: ({ orgId, ...updateData }) => ({
        url: apiEndpoints.orgNotificationSettings(orgId),
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: ["Organisation"],
    }),
    getHourReportSettings: builder.query<HourReportSettingsViewModel, string>({
      query: (orgId) => apiEndpoints.orgHourReportSettings(orgId),
      transformResponse: makeHourReportSettingsViewModel,
      providesTags: (_, __, orgId) => [{ type: "HourReportSettings", id: orgId }],
    }),
    updateHourReportSettings: builder.mutation<
      HourReportSettingsViewModel,
      { orgId: string } & Partial<HourReportSettings>
    >({
      query: ({ orgId, ...hourReportSettings }) => ({
        url: apiEndpoints.orgHourReportSettings(orgId),
        method: "PATCH",
        body: hourReportSettings,
      }),
      transformResponse: makeHourReportSettingsViewModel,
      invalidatesTags: (_, __, { orgId }) => [{ type: "HourReportSettings", id: orgId }],
      async onQueryStarted({ orgId, ...settings }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData("getHourReportSettings", orgId, (draft) => {
            Object.assign(draft.hourReportSettings, settings)
          })
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    getProjectTypes: builder.query<ProjectTypeViewModel[], string>({
      query: (id) => apiEndpoints.projectTypes(id),
      transformResponse: (projectTypes: ProjectTypeResponse[]) => {
        return projectTypes.map((projectType) => makeProjectTypeViewModel(projectType))
      },
      providesTags: ["ProjectType"],
    }),
    addProjectType: builder.mutation({
      query: ({ orgId, type }: NewProjectTypeData & { orgId: string }) => ({
        url: apiEndpoints.projectTypes(orgId),
        method: "POST",
        body: { type },
      }),
      invalidatesTags: ["ProjectType"],
    }),
    updateProjectType: builder.mutation({
      query: ({ orgId, id, ...updateData }: ProjectTypeUpdateData & { orgId: string; id: string }) => ({
        url: apiEndpoints.projectTypeItem(id, orgId),
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: ["ProjectType"],
    }),
    deleteProjectType: builder.mutation({
      query: ({ orgId, id }: { orgId: string; id: string }) => ({
        url: apiEndpoints.projectTypeItem(id, orgId),
        method: "DELETE",
      }),
      invalidatesTags: ["ProjectType"],
    }),
    renameProjectStatus: builder.mutation({
      query: ({
        orgId,
        id,
        projectTypeId,
        ...updateData
      }: ProjectCustomStatusUpdateData & { orgId: string; id: SystemStatusId; projectTypeId: string }) => ({
        url: apiEndpoints.projectStatusItem(id, projectTypeId, orgId),
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: ["ProjectType"],
    }),
    addProjectSubStatus: builder.mutation({
      query: ({
        projectStatusId,
        projectTypeId,
        orgId,
        ...subStatusData
      }: NewSubStatusData & { projectStatusId: SystemStatusId; projectTypeId: string; orgId: string }) => ({
        url: apiEndpoints.projectSubStatuses(projectStatusId, projectTypeId, orgId),
        method: "POST",
        body: subStatusData,
      }),
      invalidatesTags: ["ProjectType"],
    }),
    updateProjectSubStatus: builder.mutation({
      query: ({
        id,
        orgId,
        projectTypeId,
        projectStatusId,
        ...updateData
      }: SubStatusUpdateData & {
        id: string
        orgId: string
        projectTypeId: string
        projectStatusId: SystemStatusId
      }) => ({
        url: apiEndpoints.projectSubStatusItem(id, projectStatusId, projectTypeId, orgId),
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: ["ProjectType"],
      async onQueryStarted({ id, orgId, projectTypeId, projectStatusId, name }, { dispatch, queryFulfilled }) {
        if (name) {
          const patchResult = dispatch(
            // @ts-expect-error
            api.util.updateQueryData("getProjectTypes", orgId, (draft: ProjectTypeViewModel[]) => {
              draft.forEach((projectType) => {
                if (projectType.id === projectTypeId) {
                  projectType.projectCustomStatuses[projectStatusId].subStatuses.forEach((subStatus) => {
                    if (subStatus.id === id) {
                      subStatus.name = name
                    }
                  })
                }
              })
            })
          )
          try {
            await queryFulfilled
          } catch {
            patchResult.undo()
          }
        }
      },
    }),
    deleteProjectSubStatus: builder.mutation({
      query: ({
        id,
        orgId,
        projectTypeId,
        projectStatusId,
      }: {
        id: string
        orgId: string
        projectTypeId: string
        projectStatusId: SystemStatusId
      }) => ({
        url: apiEndpoints.projectSubStatusItem(id, projectStatusId, projectTypeId, orgId),
        method: "DELETE",
      }),
      invalidatesTags: ["ProjectType"],
    }),
    renameTaskStatus: builder.mutation({
      query: ({ orgId, id, ...updateData }: TaskCustomStatusUpdateData & { orgId: string; id: SystemStatusId }) => ({
        url: apiEndpoints.taskStatusItem(id, orgId),
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: ["Organisation"],
    }),
    addTaskSubStatus: builder.mutation({
      query: ({ orgId, id, ...updateData }: NewSubStatusData & { orgId: string; id: SystemStatusId }) => ({
        url: apiEndpoints.taskSubStatuses(id, orgId),
        method: "POST",
        body: updateData,
      }),
      invalidatesTags: ["Organisation"],
    }),
    updateTaskSubStatus: builder.mutation({
      query: ({
        id,
        statusId,
        orgId,
        ...updateData
      }: SubStatusUpdateData & { orgId: string; id: string; statusId: SystemStatusId }) => ({
        url: apiEndpoints.taskSubStatusItem(id, statusId, orgId),
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: ["Organisation"],
      async onQueryStarted({ id, statusId, orgId, name }, { dispatch, queryFulfilled }) {
        if (name) {
          const patchResult = dispatch(
            // @ts-expect-error
            api.util.updateQueryData("getOrganisationById", orgId, (draft: OrganisationViewModel) => {
              draft.taskStatuses[statusId].subStatuses.forEach((subStatus) => {
                if (subStatus.id === id) {
                  subStatus.name = name
                }
              })
            })
          )
          try {
            await queryFulfilled
          } catch {
            patchResult.undo()
          }
        }
      },
    }),
    deleteTaskSubStatus: builder.mutation({
      query: ({ id, statusId, orgId }: { orgId: string; id: string; statusId: SystemStatusId }) => ({
        url: apiEndpoints.taskSubStatusItem(id, statusId, orgId),
        method: "DELETE",
      }),
      invalidatesTags: ["Organisation"],
    }),
    getBillingSettings: builder.query<BillingSettingsResponse, string>({
      query: (orgId) => apiEndpoints.billingSettings(orgId),
      transformResponse: (response: BillingSettingsResponse) => {
        return BillingSettingsResponseSchema.parse(response)
      },
      providesTags: (_, __, orgId) => [{ type: "Billing", id: orgId }],
    }),
    saveBillingSettings: builder.mutation({
      query: ({ orgId, ...updateData }: { orgId: string } & BillingSettingsFormValues) => ({
        url: apiEndpoints.billingSettings(orgId),
        method: "PUT",
        body: {
          ...updateData.billingAddress,
          ...updateData.contactPerson,
          ...updateData.invoicingAddress,
          agreeToPolicy: updateData.agreeToPolicy,
        },
      }),
      transformResponse: (response: BillingSettingsResponse) => {
        return BillingSettingsResponseSchema.parse(response)
      },
      invalidatesTags: (_, __, { orgId }) => [{ type: "Billing", id: orgId }],
    }),
  }),
})

export const {
  useGetOrganisationByIdQuery,
  useCheckBillingSettingsQuery,
  useLazyCheckBillingSettingsQuery,
  useUpdateOrgNotificationSettingsMutation,
  useGetHourReportSettingsQuery,
  useUpdateHourReportSettingsMutation,
  useGetProjectTypesQuery,
  useAddProjectTypeMutation,
  useUpdateProjectTypeMutation,
  useDeleteProjectTypeMutation,
  useRenameProjectStatusMutation,
  useAddProjectSubStatusMutation,
  useUpdateProjectSubStatusMutation,
  useDeleteProjectSubStatusMutation,
  useRenameTaskStatusMutation,
  useAddTaskSubStatusMutation,
  useUpdateTaskSubStatusMutation,
  useDeleteTaskSubStatusMutation,
  useGetBillingSummaryQuery,
  useLazyGetBillingSummaryQuery,
  useEstimateCostQuery,
  useBillingSummaryAndCostEstimateQuery,
  useGetBillingSettingsQuery,
  useLazyGetBillingSettingsQuery,
  useSaveBillingSettingsMutation,
} = organisationApi

export type OrgNotificationSettingsUpdateData = {
  enable: NotificationUpdateAction[]
  disable: NotificationUpdateAction[]
}

export type NewProjectTypeData = {
  type: string
}

type NotificationUpdateAction = { type: NotificationType; channel: NotificationChannel }
export type ProjectTypeUpdateData = {
  type?: string
  isVisibleInOptions?: boolean
  isDefault?: boolean
}
export type ProjectCustomStatusUpdateData = {
  name: string
}

export type NewSubStatusData = {
  name: string
}
export type SubStatusUpdateData = {
  name?: string
  order?: number
  isVisibleInOptions?: boolean
}

export type TaskCustomStatusUpdateData = {
  name: string
}

export type BillingSummaryResponse = {
  total: number // total after discounts
  subtotal: number // total before discounts
  summary: Record<string, PlanSummary>
  breakdown: Breakdown[]
  discounts: string[]
  billingStatus: BillingStatus
  estimateBasedOnScenarios?: CostEstimationResponse
}

type PlanSummary = {
  name: string
  quantity: number
  price: number
  total: number
  subtotal: number
}

type Breakdown = {
  membershipId: string
  name: string
  accessLevel: string
  price: number
  priceWithDiscount: number
}

type BillingStatus = {
  seatsUsed: number
  seatLimit: number
  hasAgreedBillingPolicy: boolean
}

export type CostEstimationScenario =
  | {
      type: "addMemberRole"
      role: "orgMember" | "orgLimitedMember"
      count: number
      roleAbilityOverrides?: {
        add: string[]
        remove: string[]
      }
    }
  | {
      type: "addMemberClone"
      sourceMemberId: string
      count: number
    }

type CostEstimationResponse = {
  projectManagers: number
  basicUsers: number
  projectManagerCost: number
  basicUserCost: number
  totalCost: number
}
